import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MapPin, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, ApiError, type DeliveryAddress, type DeliveryAddresses } from "@/lib/api";
import { Button, Card, Input } from "./ui";

const EMPTY: DeliveryAddress = { name: "", street: "", zip: "", city: "", country: "DE", hours: null, notes: null, region: null };
const errMsg = (e: unknown) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Fehler") : "Fehler");

/**
 * Logiful intern: Anlieferadressen für Bestellungen der Kunden (Einkauf Pro) – Standard und je Lager abweichend.
 * Steht auf jeder Bestellung als Lieferadresse, zusammen mit Anlieferzeiten und Hinweisen.
 */
export function DeliveryAddressesCard({ admin }: { admin: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin-delivery-addresses"], queryFn: api.admin.deliveryAddresses });
  const [v, setV] = useState<DeliveryAddresses | null>(null);
  useEffect(() => { if (q.data && !v) setV(q.data.addresses); }, [q.data, v]);
  const save = useMutation({
    mutationFn: () => api.admin.setDeliveryAddresses(v!),
    onSuccess: (r) => { toast.success("Anlieferadressen gespeichert"); setV(r); void qc.invalidateQueries({ queryKey: ["admin-delivery-addresses"] }); },
    onError: (e) => toast.error("Nicht gespeichert", { description: errMsg(e) }),
  });
  if (!q.data || !v) return null;
  const extra = Object.keys(v.byWarehouse);
  const free = q.data.warehouses.filter((w) => !v.byWarehouse[w.id]);
  const nameOf = (id: string) => q.data!.warehouses.find((w) => w.id === id)?.name || id;
  return (
    <Card className="mt-4 p-4 space-y-4">
      <div className="flex items-start gap-3">
        <MapPin className="size-4 text-fg-subtle mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-[13.5px]">Anlieferadressen für Bestellungen (Einkauf Pro)</p>
          <p className="text-[12.5px] text-fg-muted">Stehen als Lieferadresse auf jeder Bestellung der Kunden an ihre Lieferanten – mit Anlieferzeiten und Hinweisen. Ohne Adresse können Kunden nicht bestellen.</p>
        </div>
      </div>
      <AddressForm title="Standard (alle Lager)" value={v.default} disabled={!admin} onChange={(a) => setV({ ...v, default: a })} />
      {extra.map((id) => (
        <AddressForm key={id} title={`Lager ${nameOf(id)}`} value={v.byWarehouse[id]!} disabled={!admin} onChange={(a) => setV({ ...v, byWarehouse: a ? { ...v.byWarehouse, [id]: a } : Object.fromEntries(Object.entries(v.byWarehouse).filter(([k]) => k !== id)) })} removable />
      ))}
      {admin && (
        <div className="flex flex-wrap items-center gap-2">
          {free.length > 0 && (
            <select defaultValue="" onChange={(e) => { if (e.target.value) setV({ ...v, byWarehouse: { ...v.byWarehouse, [e.target.value]: { ...(v.default ?? EMPTY) } } }); e.target.value = ""; }} className="h-8 rounded-lg border border-border bg-surface px-2 text-[12.5px]">
              <option value="">Abweichende Adresse für ein Lager …</option>
              {free.map((w) => <option key={w.id} value={w.id}>{w.name || w.id}</option>)}
            </select>
          )}
          <Button className="ml-auto" variant="primary" size="sm" loading={save.isPending} onClick={() => save.mutate()}>Speichern</Button>
        </div>
      )}
    </Card>
  );
}

function AddressForm({ title, value, onChange, disabled, removable }: { title: string; value: DeliveryAddress | null; onChange: (a: DeliveryAddress | null) => void; disabled: boolean; removable?: boolean }) {
  if (!value)
    return (
      <div className="rounded-xl border border-dashed border-border p-3 flex items-center gap-2 text-[13px]">
        <span className="text-fg-muted">{title}: noch keine Adresse</span>
        {!disabled && <Button size="sm" className="ml-auto" icon={<Plus className="size-3.5" />} onClick={() => onChange({ ...EMPTY })}>Adresse eintragen</Button>}
      </div>
    );
  const set = (k: keyof DeliveryAddress, x: string) => onChange({ ...value, [k]: k === "hours" || k === "notes" ? x || null : x });
  return (
    <div className="rounded-xl border border-border p-3 space-y-2">
      <div className="flex items-center gap-2">
        <p className="text-[12.5px] font-semibold">{title}</p>
        {removable && !disabled && <button className="ml-auto size-7 grid place-items-center rounded-md text-fg-subtle hover:text-danger" aria-label="Entfernen" onClick={() => onChange(null)}><Trash2 className="size-3.5" /></button>}
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <Input disabled={disabled} value={value.name} placeholder="Empfänger, z. B. Logiful GmbH – Wareneingang" onChange={(e) => set("name", e.target.value)} />
        <Input disabled={disabled} value={value.street} placeholder="Straße und Hausnummer" onChange={(e) => set("street", e.target.value)} />
        <div className="grid grid-cols-[90px_1fr_60px] gap-2 sm:col-span-2">
          <Input disabled={disabled} value={value.zip} placeholder="PLZ" onChange={(e) => set("zip", e.target.value)} />
          <Input disabled={disabled} value={value.city} placeholder="Ort" onChange={(e) => set("city", e.target.value)} />
          <Input disabled={disabled} value={value.country} placeholder="DE" onChange={(e) => set("country", e.target.value.toUpperCase().slice(0, 2))} />
        </div>
        <Input disabled={disabled} value={value.hours ?? ""} placeholder="Anlieferzeiten, z. B. Mo–Fr 7–15 Uhr" onChange={(e) => set("hours", e.target.value)} className="sm:col-span-2" />
        <textarea disabled={disabled} rows={2} value={value.notes ?? ""} placeholder="Hinweise für Lieferanten, z. B. Avis-Nummer außen auf jedes Packstück, Paletten max. 1,80 m" onChange={(e) => set("notes", e.target.value)} className="sm:col-span-2 w-full rounded-lg border border-border bg-surface p-2.5 text-[13px]" />
      </div>
    </div>
  );
}
